import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { downloadCsv, toCsv } from "@/lib/utils";
import { Fuel, Eye, Edit2, Trash2, ClipboardList, Plus, X } from "lucide-react";

type LubsPurchaseRow = {
  id: string;
  date: string;
  invoice_date?: string;
  invoice_no: string;
  vendor_id: string | null;
  vendor_name?: string;
  description: string | null;
  image_url: string | null;
  amount: number | null;
  created_at: string;
  created_by?: string;
};

type ProductRow = {
  product_id: string;
  product_name: string;
  qty: string;
  rate: string;
  item_value: number;
  discount: string;
  taxable: number;
  sgst: string;
  cgst: string;
  igst: string;
  tcs_others: string;
  amount: number;
};

export default function LubsPurchase() {
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<'invoice' | 'product'>('invoice');
  
  // Invoice Form State
  const [purchaseDate, setPurchaseDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [invoiceDate, setInvoiceDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [invoiceNo, setInvoiceNo] = useState("");
  const [description, setDescription] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Line Item Inputs State (Active typing row)
  const [productId, setProductId] = useState("");
  const [qty, setQty] = useState("");
  const [rate, setRate] = useState("");
  const [discount, setDiscount] = useState("0");
  const [sgst, setSgst] = useState("0");
  const [cgst, setCgst] = useState("0");
  const [igst, setIgst] = useState("0");
  const [tcsOthers, setTcsOthers] = useState("0");

  // Items List
  const [itemsList, setItemsList] = useState<ProductRow[]>([]);
  const [cashDiscount, setCashDiscount] = useState("0");
  const [roundingOff, setRoundingOff] = useState("0");

  // Filter State
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [filterText, setFilterText] = useState("");

  // Detailed View Mode
  const [selectedPurchase, setSelectedPurchase] = useState<LubsPurchaseRow | null>(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [purchaseItems, setPurchaseItems] = useState<any[]>([]);

  // ── Fetch Vendors ──────────────────────────────────────────────────────────
  const { data: vendors = [], isLoading: loadingVendors } = useQuery<any[]>({
    queryKey: ["/api/vendors"],
    queryFn: async () => {
      const response = await fetch("/api/vendors", { credentials: "include" });
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || "Failed to fetch vendors");
      return result.rows || [];
    },
  });

  // ── Fetch Lubricants (Products) ────────────────────────────────────────────
  const { data: products = [], isLoading: loadingProducts } = useQuery<any[]>({
    queryKey: ["/api/lubricants"],
    queryFn: async () => {
      const response = await fetch('/api/lubricants', { credentials: 'include' });
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to fetch lubricants');
      return result.rows || [];
    },
  });

  // ── Fetch Lubs Purchases ───────────────────────────────────────────────────
  const {
    data: rows = [],
    isLoading: loadingPurchases,
    refetch: refetchPurchases,
  } = useQuery<LubsPurchaseRow[]>( {
    queryKey: ["/api/lubs-purchases", filterFrom, filterTo, filterText],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filterFrom) params.append("from", filterFrom);
      if (filterTo) params.append("to", filterTo);
      if (filterText) params.append("search", filterText);

      const response = await fetch(`/api/lubs-purchases?${params.toString()}`, {
        credentials: "include",
      });
      const result = await response.json();
      if (!result.ok) throw new Error(result.error || "Failed to fetch lub purchases");
      return result.rows || [];
    },
  });

  // ── Calculations for Active Typing Row ─────────────────────────────────────
  const currentItemValue = useMemo(() => {
    const q = parseFloat(qty) || 0;
    const r = parseFloat(rate) || 0;
    return parseFloat((q * r).toFixed(3));
  }, [qty, rate]);

  const currentTaxable = useMemo(() => {
    const disc = parseFloat(discount) || 0;
    return parseFloat((currentItemValue - disc).toFixed(3));
  }, [currentItemValue, discount]);

  const currentAmount = useMemo(() => {
    const s = parseFloat(sgst) || 0;
    const c = parseFloat(cgst) || 0;
    const i = parseFloat(igst) || 0;
    const t = parseFloat(tcsOthers) || 0;
    return parseFloat((currentTaxable + s + c + i + t).toFixed(3));
  }, [currentTaxable, sgst, cgst, igst, tcsOthers]);

  // ── Summary Calculations (Including Active Row) ────────────────────────────
  const totalItemValue = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + item.item_value, 0);
    if (productId && qty && rate) base += currentItemValue;
    return base;
  }, [itemsList, productId, qty, rate, currentItemValue]);

  const totalDiscount = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.discount) || 0), 0);
    if (productId && qty && rate) base += parseFloat(discount) || 0;
    return base;
  }, [itemsList, productId, qty, rate, discount]);

  const totalIgst = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.igst) || 0), 0);
    if (productId && qty && rate) base += parseFloat(igst) || 0;
    return base;
  }, [itemsList, productId, qty, rate, igst]);

  const totalCgst = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.cgst) || 0), 0);
    if (productId && qty && rate) base += parseFloat(cgst) || 0;
    return base;
  }, [itemsList, productId, qty, rate, cgst]);

  const totalSgst = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.sgst) || 0), 0);
    if (productId && qty && rate) base += parseFloat(sgst) || 0;
    return base;
  }, [itemsList, productId, qty, rate, sgst]);

  const totalTcsOthers = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + (parseFloat(item.tcs_others) || 0), 0);
    if (productId && qty && rate) base += parseFloat(tcsOthers) || 0;
    return base;
  }, [itemsList, productId, qty, rate, tcsOthers]);

  const invoiceTotalAmount = useMemo(() => {
    let base = itemsList.reduce((sum, item) => sum + item.amount, 0);
    if (productId && qty && rate) base += currentAmount;
    
    const cd = parseFloat(cashDiscount) || 0;
    const r = parseFloat(roundingOff) || 0;
    return parseFloat((base - cd + r).toFixed(3));
  }, [itemsList, productId, qty, rate, currentAmount, cashDiscount, roundingOff]);

  // ── Add Item to itemsList ──────────────────────────────────────────────────
  const handleAddItem = () => {
    if (!productId) { toast({ title: "Please choose a product", variant: "destructive" }); return; }
    if (!qty || parseFloat(qty) <= 0) { toast({ title: "Please enter a valid quantity", variant: "destructive" }); return; }
    if (!rate || parseFloat(rate) <= 0) { toast({ title: "Please enter a valid rate", variant: "destructive" }); return; }

    const prod = products.find((p: any) => String(p.id || p._id) === productId);
    const newRow: ProductRow = {
      product_id: productId || "",
      product_name: prod?.lubricant_name || prod?.product_name || prod?.productName || prod?.name || "",
      qty: qty || "0",
      rate: rate || "0",
      item_value: currentItemValue,
      discount: discount || "0",
      taxable: currentTaxable,
      sgst: sgst || "0",
      cgst: cgst || "0",
      igst: igst || "0",
      tcs_others: tcsOthers || "0",
      amount: currentAmount
    };

    setItemsList(prev => [...prev, newRow]);

    // Reset fields
    setProductId("");
    setQty("");
    setRate("");
    setDiscount("0");
    setSgst("0");
    setCgst("0");
    setIgst("0");
    setTcsOthers("0");
  };

  // ── Update row inside itemsList ────────────────────────────────────────────
  const handleUpdateItem = (idx: number, field: keyof ProductRow, val: string) => {
    setItemsList(prev => prev.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: val };
      
      const q = parseFloat(updated.qty) || 0;
      const r = parseFloat(updated.rate) || 0;
      updated.item_value = parseFloat((q * r).toFixed(3));
      
      const disc = parseFloat(updated.discount) || 0;
      updated.taxable = parseFloat((updated.item_value - disc).toFixed(3));
      
      const s = parseFloat(updated.sgst) || 0;
      const c = parseFloat(updated.cgst) || 0;
      const i_gst = parseFloat(updated.igst) || 0;
      const t = parseFloat(updated.tcs_others) || 0;
      updated.amount = parseFloat((updated.taxable + s + c + i_gst + t).toFixed(3));
      
      return updated;
    }));
  };

  // ── Remove row from itemsList ──────────────────────────────────────────────
  const handleRemoveItem = (idx: number) => {
    setItemsList(prev => prev.filter((_, i) => i !== idx));
  };

  // ── Duplicate row ──────────────────────────────────────────────────────────
  const handleDuplicateItem = (item: ProductRow) => {
    setItemsList(prev => [...prev, { ...item }]);
    toast({ title: "Item duplicated" });
  };

  // ── Upload Image Placeholder ───────────────────────────────────────────────
  const uploadImage = async (): Promise<string | null> => {
    if (!imageFile) return null;
    if (imageFile.size > 2 * 1024 * 1024) {
      toast({ title: "Max 2MB file allowed", variant: "destructive" });
      return null;
    }
    return null;
  };

  // ── Save Invoice ───────────────────────────────────────────────────────────
  const save = async () => {
    if (!invoiceNo) { toast({ title: "Invoice No is required", variant: "destructive" }); return; }
    if (!vendorId) { toast({ title: "Vendor is required", variant: "destructive" }); return; }

    let finalItems = [...itemsList];
    if (productId && qty && rate) {
      const prod = products.find((p: any) => String(p.id || p._id) === productId);
      finalItems.push({
        product_id: productId,
        product_name: prod?.lubricant_name || prod?.product_name || prod?.productName || prod?.name || "",
        qty,
        rate,
        item_value: currentItemValue,
        discount: discount || "0",
        taxable: currentTaxable,
        sgst: sgst || "0",
        cgst: cgst || "0",
        igst: igst || "0",
        tcs_others: tcsOthers || "0",
        amount: currentAmount
      });
    }

    if (finalItems.length === 0) {
      toast({ title: "Please add at least one Product in Invoice Items tab", variant: "destructive" });
      return;
    }

    try {
      setIsSaving(true);
      const imgUrl = await uploadImage();

      const payload = {
        date: purchaseDate,
        invoice_date: invoiceDate,
        invoice_no: invoiceNo,
        description,
        vendor_id: vendorId,
        vendor_name: vendors.find((v: any) => v.id === vendorId)?.vendor_name || "",
        image_url: imgUrl,
        amount: invoiceTotalAmount,
        items: finalItems.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.qty,
          rate: item.rate,
          item_value: item.item_value,
          discount: item.discount,
          taxable: item.taxable,
          sgst: item.sgst,
          cgst: item.cgst,
          igst: item.igst,
          tcs_others: item.tcs_others,
          amount: item.amount
        })),
        item_total_value: totalItemValue,
        total_discount: totalDiscount,
        total_igst: totalIgst,
        total_cgst: totalCgst,
        total_sgst: totalSgst,
        total_tcs_others: totalTcsOthers,
        cash_discount: Number(cashDiscount) || 0,
        rounding_off: Number(roundingOff) || 0
      };

      const url = editingId ? `/api/lubs-purchases/${editingId}` : '/api/lubs-purchases';
      const method = editingId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'include'
      });

      const result = await response.json();
      if (!result.ok) throw new Error(result.error || 'Failed to save lubs purchase');

      toast({ title: "Lubricants purchase saved successfully" });
      setInvoiceNo("");
      setDescription("");
      setVendorId("");
      setItemsList([]);
      setCashDiscount("0");
      setRoundingOff("0");
      setEditingId(null);
      setActiveTab('invoice');
      refetchPurchases();
    } catch (e: any) {
      toast({ title: "Save failed", description: e.message || String(e), variant: "destructive" });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Edit Click Handler ─────────────────────────────────────────────────────
  const handleEdit = async (r: any) => {
    setEditingId(r.id);
    setInvoiceDate(r.invoice_date || r.date);
    setPurchaseDate(r.date);
    setInvoiceNo(r.invoice_no);
    setDescription(r.description || "");
    setVendorId(r.vendor_id);
    setCashDiscount(String(r.cash_discount || 0));
    setRoundingOff(String(r.rounding_off || 0));

    setLoadingItems(true);
    try {
      const response = await fetch(`/api/lubs-purchases/${r.id}/items`, { credentials: 'include' });
      const result = await response.json();
      if (result.ok && result.rows) {
        const mappedItems: ProductRow[] = result.rows.map((item: any) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          qty: String(item.quantity || 0),
          rate: String(item.rate || 0),
          item_value: Number(item.item_value || 0),
          discount: String(item.discount || 0),
          taxable: Number(item.taxable || 0),
          sgst: String(item.sgst || 0),
          cgst: String(item.cgst || 0),
          igst: String(item.igst || 0),
          tcs_others: String(item.tcs_others || 0),
          amount: Number(item.amount || 0)
        }));
        setItemsList(mappedItems);
      } else {
        setItemsList([]);
      }
    } catch (e) {
      console.error("Failed to load edit items", e);
      setItemsList([]);
    } finally {
      setLoadingItems(false);
    }

    setActiveTab('invoice');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // ── Open Full Page Detailed View ───────────────────────────────────────────
  const openInvoiceDetails = async (purchase: LubsPurchaseRow) => {
    setSelectedPurchase(purchase);
    setLoadingItems(true);
    setPurchaseItems([]);

    try {
      const response = await fetch(`/api/lubs-purchases/${purchase.id}/items`, { credentials: 'include' });
      const result = await response.json();
      if (result.ok) {
        setPurchaseItems(result.rows || []);
      } else {
        setPurchaseItems([]);
        toast({ title: "No items found for this invoice", variant: "destructive" });
      }
    } catch (e) {
      console.error("Failed to fetch purchase items", e);
      setPurchaseItems([]);
      toast({ title: "Failed to fetch details", variant: "destructive" });
    } finally {
      setLoadingItems(false);
    }
  };

  const handleExportCsv = () => {
    if (!rows || rows.length === 0) {
      toast({ title: "No data to export", variant: "destructive" });
      return;
    }
    const rowsCsv = rows.map((r, idx) => ({
      SlNo: idx + 1,
      Date: r.date,
      InvoiceNo: r.invoice_no,
      Vendor: vendors?.find((v: any) => v.id === r.vendor_id)?.vendor_name || "-",
      Description: r.description || "",
      Amount: r.amount ?? "",
    }));
    downloadCsv("lubs-purchases.csv", toCsv(rowsCsv));
  };

  // ── Detailed View Render Block ─────────────────────────────────────────────
  if (selectedPurchase) {
    return (
      <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
        {/* Breadcrumbs with Back button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-xs text-slate-500 font-medium">
            <span className="hover:underline cursor-pointer" onClick={() => setSelectedPurchase(null)}>Dashboard</span>
            <span>&gt;</span>
            <span className="hover:underline cursor-pointer" onClick={() => setSelectedPurchase(null)}>Invoice Details</span>
            <span>&gt;</span>
            <span className="text-slate-800 font-semibold">Lubricants Invoice Details</span>
          </div>
          <Button
            onClick={() => setSelectedPurchase(null)}
            className="bg-[#0e9f6e] hover:bg-[#0b8a5c] text-white font-bold px-4 h-9 rounded-lg"
          >
            &larr; Back to Invoices
          </Button>
        </div>

        {/* Detailed view card */}
        <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
          <CardContent className="p-6 space-y-6">
            {/* Header block with gray-slate background */}
            <div className="bg-[#4a606d] p-4 rounded-lg flex flex-wrap items-center justify-between gap-4 text-white text-xs font-bold shadow-inner">
              <div className="flex items-center gap-2">
                <span>Invoice Number :</span>
                <input
                  type="text"
                  readOnly
                  className="bg-white text-slate-800 px-3 py-1.5 rounded font-bold border-none w-44 text-center focus:outline-none"
                  value={selectedPurchase.invoice_no}
                />
              </div>
              <div className="flex items-center gap-2">
                <span>Date :</span>
                <input
                  type="text"
                  readOnly
                  className="bg-white text-slate-800 px-3 py-1.5 rounded font-bold border-none w-44 text-center focus:outline-none"
                  value={selectedPurchase.date ? new Date(selectedPurchase.date).toLocaleDateString('en-GB') : '-'}
                />
              </div>
              <div className="flex items-center gap-2">
                <span>Amount :</span>
                <input
                  type="text"
                  readOnly
                  className="bg-white text-slate-800 px-3 py-1.5 rounded font-extrabold border-none w-44 text-center focus:outline-none"
                  value={(Number(selectedPurchase.amount) || 0).toFixed(3)}
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-between flex-wrap gap-2 pt-2">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter:</span>
                <Input placeholder="Type to filter..." className="w-52 h-9 text-xs" />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-slate-500">Show:</span>
                <select className="h-9 rounded-md border border-slate-200 px-2 text-xs bg-white font-bold"><option>All</option></select>
                <Button variant="outline" size="sm" className="border-green-500 text-green-600 hover:bg-green-50 font-bold text-xs">CSV</Button>
                <Button variant="outline" size="sm" className="border-red-500 text-red-600 hover:bg-red-50 font-bold text-xs">PDF</Button>
              </div>
            </div>

            {/* Line Items Table */}
            {loadingItems ? (
              <div className="text-center py-12 text-slate-400">
                <span className="flex items-center justify-center gap-2">
                  <span className="h-4 w-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                  Loading line items...
                </span>
              </div>
            ) : purchaseItems.length === 0 ? (
              <div className="text-center py-12 text-slate-400">No items found for this invoice</div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="font-bold text-slate-600 h-10">S.No</TableHead>
                        <TableHead className="font-bold text-slate-600 h-10">Product Name</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Qty(Pcs)</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Rate</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Item Value</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Discount</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Taxable</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">SGST</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">CGST</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">IGST</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">TCS/Others</TableHead>
                        <TableHead className="font-bold text-slate-600 text-right h-10">Amount</TableHead>
                        <TableHead className="font-bold text-slate-600 text-center h-10">Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {purchaseItems.map((item: any, idx: number) => (
                        <TableRow key={item.id || idx} className="hover:bg-slate-50/50">
                          <TableCell className="font-medium text-slate-400">{idx + 1}</TableCell>
                          <TableCell className="font-semibold text-slate-800">{item.product_name || "Unknown"}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.quantity ? parseFloat(item.quantity).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.rate ? parseFloat(item.rate).toFixed(3) : '0.00'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.item_value ? parseFloat(item.item_value).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.discount ? parseFloat(item.discount).toFixed(3) : '0.00'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {item.taxable ? parseFloat(item.taxable).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.sgst ? parseFloat(item.sgst).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.cgst ? parseFloat(item.cgst).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.igst ? parseFloat(item.igst).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-right font-medium tabular-nums">{item.tcs_others ? parseFloat(item.tcs_others).toFixed(3) : '0.00'}</TableCell>
                          <TableCell className="text-right font-bold text-slate-800 tabular-nums">
                            {item.amount ? parseFloat(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 }) : '0.00'}
                          </TableCell>
                          <TableCell className="text-center">
                            <button
                              onClick={() => {
                                const target = selectedPurchase;
                                setSelectedPurchase(null);
                                handleEdit(target);
                              }}
                              className="text-blue-600 hover:text-blue-800 hover:underline font-bold text-xs"
                            >
                              Edit
                            </button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                
                {/* Pagination Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-xs text-slate-500 font-medium">
                  <div>
                    Showing 1 to {purchaseItems.length} of {purchaseItems.length} entries
                  </div>
                  <div className="flex items-center gap-1">
                    <button className="px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" disabled>&larr;</button>
                    <button className="px-3 py-1 bg-[#4a606d] text-white border border-[#4a606d] rounded">1</button>
                    <button className="px-2.5 py-1 border border-slate-200 rounded hover:bg-slate-50 disabled:opacity-50" disabled>&rarr;</button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-slate-50 min-h-screen">
      {/* Breadcrumbs */}
      <div className="flex items-center gap-2 text-sm text-slate-500 font-medium">
        <span className="hover:text-blue-600 cursor-pointer">Dashboard</span>
        <span>/</span>
        <span className="text-slate-800 font-semibold">Lubricants Invoice Details</span>
      </div>

      {/* Tabs */}
      <div className="flex gap-2">
        <button
          onClick={() => setActiveTab('invoice')}
          className={`px-6 py-2.5 rounded-t-lg font-bold text-sm transition-colors shadow-sm ${
            activeTab === 'invoice'
              ? 'bg-[#0e9f6e] text-white'
              : 'bg-white text-slate-700 hover:bg-slate-100 border-t border-x'
          }`}
        >
          Invoice
        </button>
        <button
          onClick={() => {
            if (!invoiceNo || !vendorId) {
              toast({
                title: "Validation Error",
                description: "Please enter Invoice No and Select Vendor first before proceeding to invoice items.",
                variant: "destructive"
              });
              return;
            }
            setActiveTab('product');
          }}
          className={`px-6 py-2.5 rounded-t-lg font-bold text-sm transition-colors shadow-sm ${
            activeTab === 'product'
              ? 'bg-[#0e9f6e] text-white'
              : 'bg-white text-slate-700 hover:bg-slate-100 border-t border-x'
          }`}
        >
          Invoice Items
        </button>
      </div>

      {/* Main Tab Content Card */}
      <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
        <CardContent className="p-6 bg-gradient-to-r from-blue-700 to-blue-800 text-white">
          {activeTab === 'invoice' ? (
            // ── Tab 1: Invoice Header Info ────────────────────────────────────
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="h-9 min-w-[12rem] px-3 flex items-center rounded-md bg-amber-400 text-slate-900 font-bold text-xs uppercase shadow-sm">
                  Choose Date
                </div>
                <Input
                  type="date"
                  className="bg-white text-slate-900 border-none w-48 h-9 text-xs font-semibold"
                  value={invoiceDate}
                  onChange={(e) => setInvoiceDate(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 items-end">
                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Invoice No *</div>
                  <Input
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Invoice No *"
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Upload Image</div>
                  <Input
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/gif"
                    onChange={(e) => setImageFile(e.target.files?.[0] || null)}
                    className="bg-white text-slate-900 border-none h-9 text-xs"
                  />
                  <div className="text-[9px] text-slate-300">
                    Allowed (JPEG, JPG, GIF, PNG) MaxSize: 2MB
                  </div>
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Description</div>
                  <Input
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Select Vendor *</div>
                  <Select value={vendorId} onValueChange={setVendorId}>
                    <SelectTrigger className="bg-white text-slate-900 border-none font-medium h-9 text-xs">
                      <SelectValue placeholder="Select Vendor" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingVendors ? (
                        <SelectItem value="loading" disabled>Loading vendors...</SelectItem>
                      ) : vendors && vendors.length > 0 ? (
                        vendors.map((v: any) => (
                          <SelectItem key={v.id || v._id} value={v.id || v._id}>
                            {v.vendor_name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-vendors" disabled>No vendors available</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  onClick={() => {
                    if (!invoiceNo || !vendorId) {
                      toast({
                        title: "Validation Error",
                        description: "Please enter Invoice No and Select Vendor first before proceeding to invoice items.",
                        variant: "destructive"
                      });
                      return;
                    }
                    setActiveTab('product');
                  }}
                  className="bg-[#0e9f6e] hover:bg-[#0b8a5c] text-white font-bold rounded-lg px-6 shadow-lg h-10 transition-transform active:scale-95"
                >
                  Next: Invoice Items &rarr;
                </Button>
              </div>
            </div>
          ) : (
            // ── Tab 2: Lubs Items Form ────────────────────────────────────────
            <div className="space-y-6">
              <h3 className="text-sm font-bold uppercase tracking-wider border-b border-white/20 pb-2 flex items-center gap-2">
                <Fuel className="h-4 w-4 text-amber-400" /> Lub InvoiceItems
              </h3>

              {/* Row 1: Line Item Inputs */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-4 items-end">
                <div className="space-y-1 xl:col-span-2">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Choose Product *</div>
                  <Select value={productId} onValueChange={setProductId}>
                    <SelectTrigger className="bg-white text-slate-900 border-none font-medium h-9 text-xs">
                      <SelectValue placeholder="Product *" />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingProducts ? (
                        <SelectItem value="loading" disabled>Loading...</SelectItem>
                      ) : products && products.length > 0 ? (
                        products.map((p: any) => (
                          <SelectItem key={p.id || p._id} value={p.id || p._id}>
                            {p.lubricant_name || p.product_name || p.productName || p.name || "Unknown"}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="no-products" disabled>No products</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Qty(Pcs)*</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Qty"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Rate*</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Rate"
                    value={rate}
                    onChange={(e) => setRate(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Item Value</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    placeholder="0"
                    value={currentItemValue || ""}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Discount</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="Disc"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Taxable</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    placeholder="0"
                    value={currentTaxable || ""}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">SGST</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="SGST"
                    value={sgst}
                    onChange={(e) => setSgst(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">CGST</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="CGST"
                    value={cgst}
                    onChange={(e) => setCgst(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">IGST</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="IGST"
                    value={igst}
                    onChange={(e) => setIgst(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase font-bold">TCS/Others</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="TCS"
                    value={tcsOthers}
                    onChange={(e) => setTcsOthers(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[11px] font-bold text-slate-200 uppercase">Amount</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    placeholder="0"
                    value={currentAmount || ""}
                  />
                </div>

                <div className="flex justify-center xl:col-span-1">
                  <Button
                    type="button"
                    onClick={handleAddItem}
                    className="bg-blue-600 hover:bg-blue-700 text-white rounded-lg h-9 w-9 p-0 flex items-center justify-center shadow animate-pulse"
                  >
                    <Plus className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Items List (Editable input rows inside Card) */}
              {itemsList.map((item, idx) => (
                <div key={idx} className="border border-white/20 rounded-xl p-4 bg-white/5 space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-12 gap-4 items-end">
                    <div className="space-y-1 xl:col-span-2">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Choose Product *</div>
                      <Select
                        value={item.product_id}
                        onValueChange={(val) => handleUpdateItem(idx, 'product_id', val)}
                      >
                        <SelectTrigger className="bg-white text-slate-900 border-none font-medium h-9 text-xs">
                          <SelectValue placeholder="Product *" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((p: any) => (
                            <SelectItem key={p.id || p._id} value={p.id || p._id}>
                              {p.lubricant_name || p.product_name || p.productName || p.name || "Unknown"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Qty(Pcs)*</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.qty}
                        onChange={(e) => handleUpdateItem(idx, 'qty', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Rate*</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.rate}
                        onChange={(e) => handleUpdateItem(idx, 'rate', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Item Value</div>
                      <Input
                        type="number"
                        readOnly
                        className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                        value={item.item_value}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Discount</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.discount}
                        onChange={(e) => handleUpdateItem(idx, 'discount', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Taxable</div>
                      <Input
                        type="number"
                        readOnly
                        className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                        value={item.taxable}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase font-bold">SGST</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.sgst}
                        onChange={(e) => handleUpdateItem(idx, 'sgst', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase font-bold">CGST</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.cgst}
                        onChange={(e) => handleUpdateItem(idx, 'cgst', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase font-bold">IGST</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.igst}
                        onChange={(e) => handleUpdateItem(idx, 'igst', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase font-bold">TCS/Others</div>
                      <Input
                        type="number"
                        className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                        value={item.tcs_others}
                        onChange={(e) => handleUpdateItem(idx, 'tcs_others', e.target.value)}
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="text-[11px] font-bold text-slate-200 uppercase">Amount</div>
                      <Input
                        type="number"
                        readOnly
                        className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                        value={item.amount}
                      />
                    </div>

                    <div className="flex gap-2 justify-center xl:col-span-1">
                      <Button
                        type="button"
                        onClick={() => handleDuplicateItem(item)}
                        className="bg-teal-600 hover:bg-teal-700 text-white rounded-lg h-9 w-9 p-0 flex items-center justify-center shadow"
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        onClick={() => handleRemoveItem(idx)}
                        className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg h-9 w-9 p-0 flex items-center justify-center shadow"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 xl:grid-cols-9 gap-4 pt-4 border-t border-white/20">
                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">Item Total Value</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    value={totalItemValue.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">Discount</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    value={totalDiscount.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">IGST</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    value={totalIgst.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">CGST</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    value={totalCgst.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">SGST</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    value={totalSgst.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">TCS/OTHER</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-slate-100 text-slate-700 border-none h-9 text-xs font-bold"
                    value={totalTcsOthers.toFixed(2)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">Cash Discount</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="0"
                    value={cashDiscount}
                    onChange={(e) => setCashDiscount(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">Rounding Off</div>
                  <Input
                    type="number"
                    className="bg-white text-slate-900 border-none h-9 text-xs font-semibold"
                    placeholder="0"
                    value={roundingOff}
                    onChange={(e) => setRoundingOff(e.target.value)}
                  />
                </div>

                <div className="space-y-1">
                  <div className="text-[10px] font-bold text-slate-200 uppercase">Invoice Total Amount *</div>
                  <Input
                    type="number"
                    readOnly
                    className="bg-amber-100 text-slate-800 border-none h-9 text-xs font-extrabold"
                    value={invoiceTotalAmount.toFixed(2)}
                  />
                </div>
              </div>

              {/* Payment Checkbox */}
              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="payment-checkbox"
                  className="rounded border-slate-300 text-teal-600 focus:ring-teal-500 w-4 h-4 cursor-pointer"
                />
                <label htmlFor="payment-checkbox" className="text-sm font-bold select-none cursor-pointer">
                  Payment
                </label>
              </div>

              {/* Bottom Action buttons */}
              <div className="flex justify-center gap-4 pt-4 border-t border-white/20">
                <Button
                  onClick={save}
                  disabled={isSaving}
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold px-12 transition-transform active:scale-95 shadow"
                >
                  {isSaving ? "SAVING..." : editingId ? "UPDATE INVOICE" : "SAVE INVOICE"}
                </Button>
                {editingId && (
                  <Button
                    variant="outline"
                    onClick={() => {
                      setEditingId(null);
                      setInvoiceNo("");
                      setDescription("");
                      setVendorId("");
                      setItemsList([]);
                      setCashDiscount("0");
                      setRoundingOff("0");
                      setActiveTab('invoice');
                    }}
                    className="rounded-full border-white/30 text-white hover:bg-white/10"
                  >
                    Cancel Edit
                  </Button>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Table Filter and Search Section ───────────────────────────────────── */}
      <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <CardContent className="p-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2 flex-grow sm:flex-none">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">From Date</span>
              <Input
                type="date"
                className="h-9 w-40 text-xs font-medium bg-white border border-slate-200 rounded-md"
                value={filterFrom}
                onChange={(e) => setFilterFrom(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 flex-grow sm:flex-none">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">To Date</span>
              <Input
                type="date"
                className="h-9 w-40 text-xs font-medium bg-white border border-slate-200 rounded-md"
                value={filterTo}
                onChange={(e) => setFilterTo(e.target.value)}
              />
            </div>
            <div className="flex items-center gap-2 flex-grow sm:flex-none">
              <span className="text-xs font-bold text-slate-500 uppercase tracking-wider whitespace-nowrap">Filter Date</span>
              <Input
                type="date"
                className="h-9 w-40 text-xs font-medium bg-white border border-slate-200 rounded-md"
              />
            </div>
            <div className="flex items-center gap-2 flex-grow sm:flex-grow">
              <Input
                placeholder="Type to filter..."
                className="h-9 text-xs font-medium bg-white border border-slate-200 rounded-md"
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
              />
              <Button
                onClick={() => refetchPurchases()}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold h-9 rounded-lg px-6 text-xs transition-colors"
              >
                Search
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Purchases List Table ──────────────────────────────────────────────── */}
      <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-4 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500">Show:</span>
              <select className="h-9 rounded-md border border-slate-200 px-2.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-400 font-bold">
                <option>All</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="border-green-500 text-green-600 hover:bg-green-50 font-bold text-xs"
                onClick={handleExportCsv}
              >
                CSV
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-red-500 text-red-600 hover:bg-red-50 font-bold text-xs"
              >
                PDF
              </Button>
              <div className="flex items-center gap-2 ml-4">
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Filter:</span>
                <Input placeholder="Type to filter..." className="w-52 h-9 text-xs" />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table className="text-xs">
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-bold text-slate-600 h-10">S.No</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Date</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Invoice No</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">GST</TableHead>
                  <TableHead className="font-bold text-slate-600 text-right h-10">Discount</TableHead>
                  <TableHead className="font-bold text-slate-600 text-right h-10">Cash Discount</TableHead>
                  <TableHead className="font-bold text-slate-600 text-right h-10">Amount</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Image</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Vendor</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">Description</TableHead>
                  <TableHead className="font-bold text-slate-600 text-center h-10">Items View</TableHead>
                  <TableHead className="font-bold text-slate-600 text-center h-10">Action</TableHead>
                  <TableHead className="font-bold text-slate-600 h-10">User Log Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingPurchases ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center text-slate-400 py-12">
                      <span className="flex items-center justify-center gap-2">
                        <span className="h-4 w-4 border-2 border-indigo-300 border-t-indigo-600 rounded-full animate-spin" />
                        Loading purchases...
                      </span>
                    </TableCell>
                  </TableRow>
                ) : !rows || rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={13} className="text-center text-slate-400 py-12">
                      No lubricants purchases found
                    </TableCell>
                  </TableRow>
                ) : rows.map((r: any, idx: number) => {
                  const vName = vendors?.find((v: any) => v.id === r.vendor_id)?.vendor_name || "-";
                  return (
                    <TableRow key={r.id || idx} className="hover:bg-slate-50 border-b border-slate-100">
                      <TableCell className="font-medium text-slate-500">{idx + 1}</TableCell>
                      <TableCell className="font-medium text-slate-700">{r.date ? new Date(r.date).toLocaleDateString('en-GB') : '-'}</TableCell>
                      <TableCell className="font-bold text-slate-800">{r.invoice_no}</TableCell>
                      <TableCell className="font-semibold text-slate-600 text-[10px]">
                        <div>CGST:{(r.total_cgst || 0).toFixed(3)}</div>
                        <div>SGST:{(r.total_sgst || 0).toFixed(3)}</div>
                        <div>IGST:{(r.total_igst || 0).toFixed(3)}</div>
                      </TableCell>
                      <TableCell className="text-right font-medium text-slate-600">
                        ₹{(r.total_discount || 0).toFixed(3)}
                      </TableCell>
                      <TableCell className="text-right font-medium text-slate-600">
                        ₹{(r.cash_discount || 0).toFixed(3)}
                      </TableCell>
                      <TableCell className="text-right font-bold text-slate-800 tabular-nums">
                        ₹{(Number(r.amount) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell className="text-center">
                        {r.image_url ? (
                          <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded font-bold text-[10px]">Yes</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="font-semibold text-slate-800">{vName}</TableCell>
                      <TableCell className="text-slate-600 font-medium">{r.description || '-'}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openInvoiceDetails(r)}
                          className="text-blue-600 hover:text-blue-800 hover:bg-blue-50 p-1.5 rounded-lg"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex gap-1.5 justify-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(r)}
                            className="border-slate-200 text-blue-600 hover:bg-blue-50 p-1.5 h-8 w-8 rounded-lg"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (!confirm('Delete this lubs purchase invoice?')) return;
                              try {
                                const response = await fetch(`/api/lubs-purchases/${r.id}`, { method: 'DELETE', credentials: 'include' });
                                const result = await response.json();
                                if (result.ok) {
                                  toast({ title: "Success", description: "Purchase invoice deleted" });
                                  refetchPurchases();
                                } else {
                                  throw new Error(result.error || 'Failed to delete');
                                }
                              } catch (error: any) {
                                toast({ variant: "destructive", title: "Error", description: error.message || 'Failed to delete' });
                              }
                            }}
                            className="border-slate-200 text-rose-600 hover:bg-rose-50 p-1.5 h-8 w-8 rounded-lg"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
                        {r.created_by ? `Created: ${r.created_by}` : ''} {r.created_at ? new Date(r.created_at).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          
          <div className="flex justify-between items-center text-xs text-slate-500 pt-4 border-t border-slate-100">
            <span>Showing 1 to {rows.length} of {rows.length} entries</span>
            <div className="flex gap-1">
              <Button variant="outline" size="sm" disabled>Previous</Button>
              <Button variant="outline" size="sm" className="bg-blue-50 text-blue-600 border-blue-200">1</Button>
              <Button variant="outline" size="sm" disabled>Next</Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

